/* stylelint-disable custom-property-pattern */
<!-- eslint-disable no-mixed-operators -->
<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script setup lang="ts">
    import FloatingActionButton from '@/components/FloatingActionButton.ce.vue';
    import PartnerButtons from '@/components/PartnerButtons.ce.vue';
    import IndexView from '@/views/index.ce.vue';
    import { FormKitRoot } from '@formkit/vue';
    import { GOTO_SECTION, WIDGET_CLOSE, WIDGET_NAVIGATE, WIDGET_OPEN, WIDGET_TOGGLE } from './constants/events';
    import { WidgetPositionEnum } from './constants/style';
    import { FRAME_TO_HOST, buildMessage } from './frame/bridge';
    import { type EventBusType } from './plugins/event-bus-plugin';
    import { type PartnerCategory, type TargetSection, type WidgetProps } from './typings/widget';

    const props = defineProps<WidgetProps>();

    // Stores
    const { resetPathStore } = useWidgetPathStore();
    const { isMenuOpen, isQuoteOpen } = storeToRefs(useWidgetMenuStore());
    const { openWindow } = storeToRefs(useWidgetVenueStore());
    const trackingStore = useWidgetTrackingStore();

    // Constants

    // Covers the button's own edge offsets in the reported iframe size so it is never clipped.
    const LAUNCHER_MARGIN = 48;

    // Composables
    const eventBus = inject<EventBusType>('eventBus');

    // Refs
    const floatingButton: Ref<HTMLElement | null> = ref(null);

    // While a partner label is expanded the launcher collapses to a square and gives up the
    // width it takes - the row is edge-anchored, so otherwise the launcher gets shoved sideways.
    const isPartnerExpanded: Ref<boolean> = ref(false);

    // Computed

    /*
     * Collapsing is only safe while the launcher is the trailing element of the row - then
     * giving up width moves nothing but itself. It trails naturally on right anchors, and on
     * left anchors because `row-reverse` (below) puts it on the free side. If it instead sat
     * between the anchor and the partner buttons, the shrink would drag the hovered button out
     * from under the cursor - un-hovering it, un-collapsing the launcher, dragging it back: a
     * flicker loop. Centre is that loop at half amplitude with no safe side (translateX(-50%)
     * shifts the whole row on any width change), so it never collapses.
     */
    const canCollapseLauncher: ComputedRef<boolean> = computed(
        () => !(props.position ?? WidgetPositionEnum.BOTTOM_RIGHT).includes('center'),
    );

    // Truthy, not strict boolean: the attribute arrives bare (cast to true) or as a string "1".
    const enabledPartnerCategories: ComputedRef<PartnerCategory[]> = computed(() => ([
        props.showTableButton && 'table',
        props.showHotelButton && 'hotel',
        props.showGiftButton && 'gift',
    ].filter(Boolean) as PartnerCategory[]));

    const hasPartnerButtons: ComputedRef<boolean> = computed(() => enabledPartnerCategories.value.length > 0);

    // Methods
    const closeWidget = (): void => {
        resetPathStore();
        openWindow.value = false;
        isMenuOpen.value = false;
        isQuoteOpen.value = false;
    };

    const openWidget = (): void => {
        openWindow.value = true;
    };

    const toggleWidget = (): void => {
        openWindow.value = !openWindow.value;

        if (openWindow.value) {
            resetPathStore();
            isMenuOpen.value = false;
            isQuoteOpen.value = false;
        }
    };

    const openWidgetTarget = async(target: TargetSection, payload?: Record<string, unknown>): Promise<void> => {
        if (!openWindow.value){
            openWindow.value = true;
            await nextTick();
        }
        eventBus?.emit(GOTO_SECTION, {
            target,
            payload,
        });
    };

    const navigateWidget = (params?: { target: TargetSection, payload?: Record<string, unknown> }): void => {
        if (!params?.target) return;

        const { target, payload } = params;

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        openWidgetTarget(target, payload);
    };

    // Register widget event handlers
    const registerGlobalEvents = (): void => {
        eventBus?.on(WIDGET_CLOSE, closeWidget);
        eventBus?.on(WIDGET_OPEN, openWidget);
        eventBus?.on(WIDGET_TOGGLE, toggleWidget);
        eventBus?.on(WIDGET_NAVIGATE, navigateWidget);
    };

    /*
     * The widget renders inside a cross-origin, transparent iframe owned by the host shell
     * (see frame/host-shell.ts). The iframe always spans the full viewport - so the frame's
     * media queries and responsive tokens match the host page - and while closed the shell
     * clips it to just the launcher so the host page stays usable. We report that box (in
     * viewport coordinates) whenever the open state, button size or viewport changes.
     * No-op on localhost (not framed).
     */
    const postGeometry = (): void => {
        if (window.parent === window) return;

        const rect = openWindow.value ? null : floatingButton.value?.getBoundingClientRect();

        window.parent.postMessage(buildMessage(FRAME_TO_HOST.RESIZE, {
            open: openWindow.value,
            top: (rect?.top ?? 0) - LAUNCHER_MARGIN,
            left: (rect?.left ?? 0) - LAUNCHER_MARGIN,
            width: rect ? rect.width + LAUNCHER_MARGIN * 2 : 0,
            height: rect ? rect.height + LAUNCHER_MARGIN * 2 : 0,
        }), '*');
    };

    onMounted(async(): Promise<void> => {
        // Event-bus handlers are driven by the host shell (via postMessage) in production
        // and by the local CONSTELL shim in dev.
        registerGlobalEvents();

        await nextTick();
        postGeometry();

        // Re-report when the button resizes (e.g. label/locale change) OR first appears.
        // The button is `v-if="venue"`, so on an empty-then-set venue the ref is null at
        // mount; useResizeObserver watches the ref and fires on first observe, so attaching
        // it unconditionally reports the real size once the button renders (instead of the
        // iframe staying stuck at the 48x48 launcher fallback).
        useResizeObserver(floatingButton, postGeometry);

        // The launcher is edge-anchored, so its viewport coordinates move on every resize
        // even when its size does not - re-report or the clip box goes stale.
        useEventListener(window, 'resize', postGeometry);
    });

    onBeforeUnmount((): void => {
        eventBus?.off(WIDGET_CLOSE, closeWidget);
        eventBus?.off(WIDGET_OPEN, openWidget);
        eventBus?.off(WIDGET_TOGGLE, toggleWidget);
        eventBus?.off(WIDGET_NAVIGATE, navigateWidget);
    });

    // Watchers

    /*
     * `openWindow` is the single source of truth for widget visibility - the one chokepoint
     * covering the FAB, overlay, CONSTELL.open/close/toggle and the "Sluiten" button.
     */
    watch(() => openWindow.value, (isOpen: boolean) => {
        // PartnerButtons is v-if'd out while open, so it never fires mouseleave/focusout -
        // clear the flag or the launcher stays square as it becomes the close button.
        isPartnerExpanded.value = false;

        // Keep the host iframe in sync with the open/closed state.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        nextTick(postGeometry);

        // Analytics: one session per open/close cycle.
        if (isOpen) trackingStore.startSession();
        else trackingStore.endSession();
    });
</script>

<template>
    <div
        v-if="venue"
        :class="position"
        :style="{
            '--widget-offset-x': `${offsetX}px`,
            '--widget-offset-y': `${offsetY}px`,
            ...(brandColor && { '--widget-brand-color': brandColor }),
        }"
        class="widget-root"
    >
        <FormKitRoot>
            <div
                v-if="openWindow"
                class="overlay"
            />
            <div
                v-if="!openWindow"
                ref="floatingButton"
                class="floating-button"
            >
                <FloatingActionButton
                    :open-state-label="buttonLabelClose"
                    :closed-state-label="buttonLabel"
                    :brand-color="brandColor"
                    :is-window-open="openWindow"
                    :collapsed="isPartnerExpanded && canCollapseLauncher"
                    @let-the-window-grow="toggleWidget"
                />
                <PartnerButtons
                    v-if="hasPartnerButtons"
                    :brand-color="brandColor"
                    :categories="enabledPartnerCategories"
                    @expanded-change="isPartnerExpanded = $event"
                />
            </div>
            <div
                id="floating-window"
                :class="{ open: openWindow, close: !openWindow, menu: isMenuOpen, quote: isQuoteOpen }"
                class="floating-window"
            >
                <Transition name="fade">
                    <IndexView
                        v-show="openWindow"
                        :is-open="openWindow"
                        :venue="venue"
                        :locale="locale ?? 'nl'"
                        :max-guest-amount="maxGuestAmount"
                        :min-guest-amount="minGuestAmount"
                        :guest-amount-step="guestAmountStep"
                        :max-budget-amount="maxBudgetAmount"
                        :min-budget-amount="minBudgetAmount"
                        :budget-step="budgetStep"
                        :brand-color="brandColor"
                        @close="toggleWidget"
                    />
                </Transition>
            </div>
        </FormKitRoot>
    </div>
</template>

<style scoped lang="scss">
.overlay {
    position: fixed;
    z-index: var(--zindex-all-top);
    background: var(--color-black-space-opacity-64);
    block-size: 100%;
    inline-size: 100%;
    inset-block-start: 0;
    inset-inline-start: 0;
}

.floating-button {
    position: fixed;
    z-index: calc(var(--zindex-all-top) + 1);
    display: flex;
    align-items: center;
}

.floating-window {
    position: fixed;
    z-index: calc(var(--zindex-all-top) + 1);
    overflow: hidden;
    block-size: 0;
    box-shadow: 0 4px 124px 0 rgb(0 0 0 / 0.12);
    inline-size: 0;
    opacity: 0;
    transform: scale(1);
    transition: opacity 1s cubic-bezier(0.2, 0.8, 0.2, 1), inline-size 250ms cubic-bezier(0.2, 0.8, 0.2, 1), max-inline-size 250ms cubic-bezier(0.2, 0.8, 0.2, 1);

    &.open {
        block-size: calc(var(--widget-height));
        inline-size: 100%;
        opacity: 1;

        @include min-width(md) {
            max-inline-size: var(--widget-max-width-quote-mobile);
        }

        @include min-width(lg) {
            max-inline-size: var(--widget-max-width-quote);
        }

        &.menu {
            @include min-width(md) {
                max-inline-size: var(--widget-max-width-menu-mobile);
            }

            @include min-width(lg) {
                max-inline-size: var(--widget-max-width-menu);
            }
        }
    }

    @include min-width(md) {
        border-radius: var(--border-radius-md);
    }

    @include min-width(lg) {
        max-inline-size: var(--widget-max-width);
    }
}

// Positions

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.bottom-right {
    .floating-button {
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-end: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-end: 0;
        inset-inline-end: 0;

        @include min-width(md) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) - var(--spacing-03) + var(--widget-offset-y));
            inset-inline-end: var(--widget-button-base-offset-x);
        }

        @include min-width(lg) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) - var(--spacing-03) + var(--widget-offset-y));
            inset-inline-end: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.bottom-center {
    .floating-button {
        // Partner buttons first, launcher on the end - see the collapse note in <script>.
        flex-direction: row-reverse;
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: 50%;
        transform: translateX(-50%);
    }

    .floating-window {
        inset-block-end: 0;

        @include min-width(md) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) - var(--spacing-03) + var(--widget-offset-y));
            inset-inline-start: 50%;
            transform: translateX(-50%);
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.bottom-left {
    .floating-button {
        // Partner buttons first; the launcher trails on the free side, so its collapse is
        // safe here too - see the collapse note in <script>.
        flex-direction: row-reverse;
        inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-end: 0;
        inset-inline-start: 0;

        @include min-width(md) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) - var(--spacing-03) + var(--widget-offset-y));
            inset-inline-start: var(--widget-button-base-offset-x);
        }

        @include min-width(lg) {
            inset-block-end: calc(var(--widget-button-base-offset-y) + var(--widget-base-offset-y) - var(--spacing-03) + var(--widget-offset-y));
            inset-inline-start: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.top-right {
    .floating-button {
        inset-block-start: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-end: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-start: 0;
        inset-inline-end: 0;

        @include min-width(md) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y));
            inset-inline-end: var(--widget-button-base-offset-x);
        }

        @include min-width(lg) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y));
            inset-inline-end: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.top-center {
    .floating-button {
        // Partner buttons first, launcher on the end - see the collapse note in <script>.
        flex-direction: row-reverse;
        inset-block-start: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: 50%;
        transform: translateX(-50%);
    }

    .floating-window {
        inset-block-start: 0;
        inset-inline-start: 0;

        @include min-width(md) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y));
            inset-inline-start: 50%;
            transform: translateX(-50%);
        }

        @include min-width(lg) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y));
        }
    }
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.top-left {
    .floating-button {
        // Partner buttons first; the launcher trails on the free side, so its collapse is
        // safe here too - see the collapse note in <script>.
        flex-direction: row-reverse;
        inset-block-start: calc(var(--widget-button-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: calc(var(--widget-button-base-offset-x) + var(--widget-offset-x));
    }

    .floating-window {
        inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y));
        inset-inline-start: 0;

        @include min-width(md) {
            inset-block-start: calc(var(--widget-base-offset-y) + var(--widget-offset-y));
            inset-inline-start: var(--widget-button-base-offset-x);
        }

        @include min-width(lg) {
            inset-inline-start: calc(var(--widget-base-offset-x) + var(--widget-offset-x));
        }
    }
}

:deep(.formkit-outer .formkit-inner button .formkit-selection-wrapper span),
:deep(.formkit-outer .formkit-inner .dropdown__list ul li .option span),
:deep(.formkit-outer .formkit-inner input),
:deep(.formkit-outer .formkit-inner input::placeholder),
:deep(.formkit-outer .formkit-inner textarea),
:deep(.formkit-outer .formkit-inner textarea::placeholder)  {
    /* stylelint-disable-next-line declaration-no-important */
    font-size: var(--font-medium) !important;
}
</style>
